/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'

import { createPortal } from 'react-dom'

import Dropdown from '@/components/ui/dropdown/Dropdown'

export interface SavedViewPopoverProps {
  /** The view the page is showing, or null for none ("Default"). */
  selected: { name: string; isDefault?: boolean } | null
  /** Every saved view's name, for the switcher. */
  viewNames: string[]
  /** The page has changed since the selected view was loaded or saved. */
  isDirty: boolean
  /** Saves the page as a new view and selects it. Returns an error message, or null once saved. */
  onSaveAsNew: (name: string, isDefault: boolean) => string | null
  /** Overwrites the selected view with the page as it is now. */
  onSaveSelected: () => void
  /** Whether the selected view opens when the page loads. */
  onSetDefault: (isDefault: boolean) => void
  /** Deletes the selected view; the page keeps its current state, now as no view. */
  onDelete: () => void
  onSelect: (name: string) => void
  onReset: () => void
  /** What Reset does on this page. */
  resetHelp: ReactNode
}

/**
 * Saved views, kept out of the way: a link in the page's top row ("Saved Views", or the view's
 * name) opening a small panel with everything else. Most users have one company and never save a
 * view.
 */
export const SavedViewPopover = ({
  selected,
  viewNames,
  isDirty,
  onSaveAsNew,
  onSaveSelected,
  onSetDefault,
  onDelete,
  onSelect,
  onReset,
  resetHelp,
}: SavedViewPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const [newName, setNewName] = useState('')
  const [newIsDefault, setNewIsDefault] = useState(false)
  const [error, setError] = useState('')
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const linkRef = useRef<HTMLButtonElement>(null)

  const close = () => setIsOpen(false)

  const open = () => {
    setNewName('')
    setNewIsDefault(false)
    setError('')
    setConfirmingDelete(false)
    setIsOpen(true)
  }

  // Sits just under the link. Rendered on document.body (like the dropdown's menu), so it isn't
  // clipped by the toolbar and a click in that menu doesn't count as a click outside.
  useLayoutEffect(() => {
    if (!isOpen || !linkRef.current) return
    const rect = linkRef.current.getBoundingClientRect()
    setPosition({ top: rect.bottom + 4, left: rect.left })
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', close)
    }
  }, [isOpen])

  const handleSaveNew = () => {
    const message = onSaveAsNew(newName, newIsDefault)
    if (message) {
      setError(message)
    } else {
      close()
    }
  }

  const options = viewNames.map((name) => ({ value: name, label: name }))

  return (
    <>
      <button
        ref={linkRef}
        type="button"
        className="link link-hover text-sm shrink-0 whitespace-nowrap"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => (isOpen ? close() : open())}
        title={isDirty ? 'Changed since this view was saved' : undefined}
      >
        {selected ? selected.name : 'Saved Views'}
        {isDirty && <span aria-label="unsaved changes"> •</span>}
      </button>

      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9000]" data-testid="saved-view-backdrop" onMouseDown={close} />
            <div
              role="dialog"
              aria-label="Saved views"
              className="fixed z-[9001] w-100 bg-base-100 text-base-content rounded-box shadow-xl border border-base-300 p-4 space-y-4 text-sm"
              style={{ top: position.top, left: position.left }}
            >
              <div>
                <span className="opacity-70">Current view: </span>
                <span className="font-semibold">{selected ? selected.name : 'Default'}</span>
                {isDirty && <div className="text-xs text-warning mt-0.5">Unsaved changes</div>}
              </div>

              {selected ? (
                <div className="space-y-2">
                  {confirmingDelete ? (
                    <div className="flex items-center gap-2">
                      <span className="flex-1">Delete {selected.name}?</span>
                      <button
                        type="button"
                        className="btn btn-error btn-sm"
                        onClick={() => {
                          onDelete()
                          setConfirmingDelete(false)
                        }}
                      >
                        Confirm
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmingDelete(false)}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={!isDirty}
                        onClick={() => {
                          onSaveSelected()
                          close()
                        }}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm text-error"
                        onClick={() => setConfirmingDelete(true)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={Boolean(selected.isDefault)}
                      onChange={(e) => onSetDefault(e.target.checked)}
                    />
                    <span>Open this view by default</span>
                  </label>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="block">
                    <span className="block mb-1">Save as new view</span>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="input input-bordered input-sm flex-1 min-w-0"
                        placeholder="View name"
                        value={newName}
                        autoFocus
                        onChange={(e) => {
                          setNewName(e.target.value)
                          setError('')
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveNew()
                        }}
                      />
                      <button type="button" className="btn btn-primary btn-sm" onClick={handleSaveNew}>
                        Save
                      </button>
                    </div>
                  </label>
                  {error && <p className="text-error text-xs">{error}</p>}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={newIsDefault}
                      onChange={(e) => setNewIsDefault(e.target.checked)}
                    />
                    <span>Open this view by default</span>
                  </label>
                </div>
              )}

              <div>
                {options.length > 0 ? (
                  <>
                    <span className="block mb-1">Switch to a saved view</span>
                    <Dropdown
                      options={options}
                      selectedItem={options.find((o) => o.value === selected?.name) ?? null}
                      handleSingleSelectItem={(option) => {
                        if (option) onSelect(option.value)
                        close()
                      }}
                      placeholder="Select..."
                    />
                  </>
                ) : (
                  <p className="opacity-70">No saved views exist</p>
                )}
              </div>

              <div className="border-t border-base-300 pt-3">
                <button
                  type="button"
                  className="btn btn-outline btn-sm mb-1"
                  onClick={() => {
                    onReset()
                    close()
                  }}
                >
                  Reset to defaults
                </button>
                <p className="text-xs opacity-70">{resetHelp}</p>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  )
}
